/**
 * Sección "Autorizados de portal" de la cola de Validaciones (2026-09-03).
 *
 * Quinto tipo de validable. Va en su propio componente y **sin selección
 * masiva**, a diferencia de residentes/vehículos/accesos: designar un
 * autorizado es raro —uno o dos pendientes a la vez— y aprobar en lote algo
 * que crea cuentas de portal invita a apretar sin leer. Justamente lo que este
 * flujo viene a evitar.
 *
 * Lo que la Comisión necesita ver para decidir está todo en la fila,
 * empezando por la **justificación** del titular: es el dato por el que existe
 * este flujo.
 */
import { useState } from 'react';
import type { DesignacionAutorizadoPendiente } from '../../lib/api/validaciones';
import { Button } from '../ui/Button';
import { ModalConfirmar } from '../ui/ModalConfirmar';
import { formatearFechaHora } from '../../lib/tiempo';

const TH =
  'text-left font-medium py-2 px-2 whitespace-nowrap';

const ESTADO_LABEL: Record<string, string> = {
  pendiente: 'En revisión',
  aprobada: 'Aprobada',
  rechazada: 'No aprobada',
};

export interface DesignacionesAutorizadoProps {
  designaciones: DesignacionAutorizadoPendiente[];
  onAprobar: (id: string) => Promise<void>;
  onRechazar: (id: string, motivo: string) => Promise<void>;
}

export function DesignacionesAutorizado({
  designaciones,
  onAprobar,
  onRechazar,
}: DesignacionesAutorizadoProps) {
  const [enCurso, setEnCurso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rechazando, setRechazando] =
    useState<DesignacionAutorizadoPendiente | null>(null);

  if (designaciones.length === 0) return null;

  async function ejecutar(id: string, accion: () => Promise<void>) {
    setEnCurso(id);
    setError(null);
    try {
      await accion();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo completar.');
    } finally {
      setEnCurso(null);
    }
  }

  return (
    <section>
      <h2 className="text-lg font-medium text-text-primary mb-1">
        Autorizados de portal ({designaciones.length})
      </h2>
      <p className="text-xs text-text-secondary mb-3">
        El titular designó a alguien de su casa para administrar el portal.
        Al aprobar se le crea la cuenta y se le avisa por correo.
      </p>

      {error && (
        <div
          role="alert"
          className="mb-3 bg-state-danger/10 border-[0.5px] border-state-danger/30 text-state-danger rounded-md p-3 text-sm"
        >
          {error}
        </div>
      )}

      <div className="overflow-x-auto border-[0.5px] border-white/[0.08] rounded-md">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-text-muted border-b border-white/[0.08]">
              <th className={TH}>Casa</th>
              <th className={TH}>A quién designa</th>
              <th className={TH}>Quién designa</th>
              <th className={TH}>Motivo</th>
              <th className={TH}>Solicitado</th>
              <th className="text-right font-medium py-2 px-2 w-44"></th>
            </tr>
          </thead>
          <tbody>
            {designaciones.map((d) => (
              <tr
                key={d.id}
                className="border-b border-white/[0.05] last:border-0 align-top"
              >
                <td className="py-2 px-2 whitespace-nowrap">
                  {d.lote_identificador}
                </td>
                <td className="py-2 px-2">
                  <div className="text-text-primary">{d.residente_nombre}</div>
                  <div className="text-text-muted">
                    {d.residente_parentesco}
                    {d.residente_email ? ` · ${d.residente_email}` : ''}
                  </div>
                </td>
                <td className="py-2 px-2">{d.titular_nombre}</td>
                {/* El motivo NO se trunca: es lo que la Comisión tiene que
                    leer para decidir. */}
                <td className="py-2 px-2 max-w-xs whitespace-pre-line">
                  {d.justificacion}
                </td>
                <td className="py-2 px-2 whitespace-nowrap text-text-muted">
                  {formatearFechaHora(d.solicitado_at)}
                </td>
                <td className="py-2 px-2 text-right whitespace-nowrap">
                  {d.estado === 'pendiente' ? (
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={enCurso !== null}
                        onClick={() =>
                          void ejecutar(d.id, () => onAprobar(d.id))
                        }
                      >
                        {enCurso === d.id ? 'Aprobando…' : 'Aprobar'}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={enCurso !== null}
                        onClick={() => setRechazando(d)}
                      >
                        Rechazar
                      </Button>
                    </div>
                  ) : (
                    <div className="text-text-muted">
                      <div>{ESTADO_LABEL[d.estado] ?? d.estado}</div>
                      {d.revisado_por_nombre && (
                        <div className="text-[10px]">
                          por {d.revisado_por_nombre}
                        </div>
                      )}
                      {d.motivo_rechazo && (
                        <div className="text-[10px] whitespace-pre-line">
                          {d.motivo_rechazo}
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rechazando && (
        <ModalConfirmar
          title="No aprobar la designación"
          mensaje={
            `${rechazando.titular_nombre} designó a ` +
            `${rechazando.residente_nombre} para administrar la Casa ` +
            `${rechazando.lote_identificador}. El motivo del rechazo se lo ` +
            'mostramos al titular: si no sabe por qué, va a volver a designar ' +
            'a la misma persona.'
          }
          pedirMotivo
          motivoPlaceholder="Por qué no se aprueba"
          textoConfirmar="No aprobar"
          variantConfirmar="danger"
          onConfirmar={async (motivo) => {
            const d = rechazando;
            setRechazando(null);
            await ejecutar(d.id, () => onRechazar(d.id, motivo));
          }}
          onCancelar={() => setRechazando(null)}
        />
      )}
    </section>
  );
}
